import React from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { useI18n } from '@runtime/core/i18n/i18n'
import { selectPosterOpen, useAppStore } from '@runtime/core/store/appStore'
import { useUrlThemeSelection } from '@runtime/core/theme/useUrlThemeSelection'
import { useUser } from '@runtime/core/user/userContext'

import PlayErrorBoundary from '@/core/resilience/PlayErrorBoundary'
import PlayNotFound from '@/core/resilience/PlayNotFound'
import PlayMetadata from '@/core/resilience/PlayMetadata'
import PlayShell from '@/components/layout/playShell/PlayShell'
import PlayRouteFocus from '@/components/layout/playShell/PlayRouteFocus'
import PlayRoutePending from '@/components/layout/playShell/PlayRoutePending'
import PwaUpdatePrompt from '@/core/pwa/PwaUpdatePrompt'
import { CoachProvider } from '@/features/coach/CoachContext'
import CoachLauncherDock from '@/features/coach/CoachLauncherDock'
import CoachPanel from '@/features/coach/CoachPanel'
import { isGatewayPath } from '@/routes/gateway/gatewayPaths'
import HomeArrivalRoute from '@/routes/home/arrival/HomeArrivalRoute'
import { HOME_ALIASES, isHomeArrivalPath } from '@/routes/home/arrival/homeArrival'
import type { OnlinePuzzleMeta } from '@/routes/online/logic/onlineTypes'
import type { StrategyRoutePreset } from '@/routes/strategy/strategyRoute.utils'
import appPackage from '../../package.json'
import styles from './App.module.scss'
import PlayAccountOverlays from './PlayAccountOverlays'

const { useEffect, useState, lazy, Suspense } = React

const StrategyRoute = lazy(() => import('@/routes/strategy/StrategyRoute'))
const PuzzleRoute = lazy(() => import('@/routes/puzzle/PuzzleRoute'))
const ArcadeRoute = lazy(() => import('@/routes/arcade/views/ArcadeRoute'))
const OnlineRoute = lazy(() => import('@/routes/online/OnlineRoute'))
const ChallengesRoute = lazy(() => import('@/routes/challenges/ChallengesRoute'))
const RankingRoute = lazy(() => import('@/routes/ranking/RankingRoute'))
const PrivacyRoute = lazy(() => import('@/routes/privacy/PrivacyRoute'))
const RulesRoute = lazy(() => import('@/routes/rules/RulesRoute'))
const EducationRoute = lazy(() => import('@/routes/education/EducationRoute'))
const ToolsRoute = lazy(() => import('@/routes/tools/ToolsRoute'))

type NavState = {
  preset?: StrategyRoutePreset
  onlinePuzzle?: OnlinePuzzleMeta
}

export default function App() {
  const navigate = useNavigate()
  const location = useLocation()
  const isGateway = isGatewayPath(location.pathname)
  const { t } = useI18n()
  const [isAppUpdateDownloading, setIsAppUpdateDownloading] = useState(false)

  const { loginOpen } = useUser()

  const posterOpen = useAppStore(selectPosterOpen)
  const setPosterOpen = useAppStore(s => s.setPosterOpen)

  const state = (location.state || {}) as NavState
  const preset = state.preset ?? undefined

  useUrlThemeSelection(!isHomeArrivalPath(location.pathname))

  // Expose a tiny global helper for legacy parts of the UI
  // (some screens still call window.openPoster())
  useEffect(() => {
    window.openPoster = () => setPosterOpen(true)

    return () => {
      try {
        delete window.openPoster
      } catch {
        window.openPoster = undefined
      }
    }
  }, [setPosterOpen])

  const goBackOrHome = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/hub')
    }
  }

  return (
    <CoachProvider>
      <PlayMetadata />
      <PlayShell gateway={isGateway} className={styles.playShell}>
        <div className="app-body">
          {!isGateway && <CoachLauncherDock fallback />}
          <PlayErrorBoundary>
            <Suspense fallback={<PlayRoutePending />}>
              <PlayRouteFocus>
                <Routes>
                  {HOME_ALIASES.map(path => (
                    <Route key={path} path={path} element={<HomeArrivalRoute />} />
                  ))}
                  <Route path="/p/:publicSlug" element={<HomeArrivalRoute />} />
                  <Route path="/hub/admin/*" element={<Navigate to="/hub" replace />} />

                  <Route
                    path="/strategy/*"
                    element={<StrategyRoute onBack={goBackOrHome} preset={preset} />}
                  />

                  <Route path="/puzzle/*" element={<PuzzleRoute onBack={goBackOrHome} />} />

                  <Route
                    path="/arcade/*"
                    element={<ArcadeRoute onPoster={() => setPosterOpen(true)} />}
                  />

                  {/* Canonical : /online (compat redirect from old path) */}
                  <Route path="/online/browser" element={<Navigate to="/online" replace />} />

                  <Route
                    path="/online/*"
                    element={
                      <OnlineRoute
                        onLaunchLocal={(nextPreset: StrategyRoutePreset) => {
                          navigate('/strategy', { state: { preset: nextPreset } })
                        }}
                        onLaunchPuzzle={(meta: OnlinePuzzleMeta) => {
                          const nextPath =
                            meta.mode === 'posterRace'
                              ? '/puzzle/poster-online'
                              : meta.mode === 'unitedRace'
                                ? '/puzzle/united-online'
                                : '/puzzle/poster-switch-online'
                          navigate(nextPath, { state: { onlinePuzzle: meta } })
                        }}
                      />
                    }
                  />

                  <Route path="/challenges" element={<ChallengesRoute onBack={goBackOrHome} />} />
                  <Route
                    path="/challenges/s/*"
                    element={<ChallengesRoute onBack={goBackOrHome} />}
                  />

                  <Route path="/ranking" element={<RankingRoute onBack={goBackOrHome} />} />

                  <Route path="/tools/*" element={<ToolsRoute onBack={goBackOrHome} />} />

                  <Route path="/privacy" element={<PrivacyRoute />} />
                  <Route path="/rules/*" element={<RulesRoute />} />
                  <Route path="/education" element={<EducationRoute />} />

                  <Route path="*" element={<PlayNotFound />} />
                </Routes>
              </PlayRouteFocus>
            </Suspense>
          </PlayErrorBoundary>

          <PlayAccountOverlays
            posterOpen={posterOpen}
            loginOpen={loginOpen}
            onClosePoster={() => setPosterOpen(false)}
          />

          {/* PWA update prompt (prod only, SW disabled in dev). */}
          <PwaUpdatePrompt onUpdateDownloadChange={setIsAppUpdateDownloading} />
          {!isGateway && <CoachPanel />}
        </div>

        {!isGateway && (
          <div className="app-version" aria-live="polite">
            {isAppUpdateDownloading && (
              <span
                className="app-version-update"
                role="img"
                aria-label={t('runtime.pwaUpdate.downloadingAria')}
                data-testid="app-version-update"
              />
            )}
            <span>v{appPackage.version}</span>
          </div>
        )}
      </PlayShell>
    </CoachProvider>
  )
}
